import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FilterStaticBase } from '@portal/shared/ui/filter/src/lib/components/static-base';
import {
  FILTER_CONTENT_ITEM,
  ISelectInput,
  SearchSelector,
  SearchOperator,
} from '@portal/shared/ui/filter';
import { ProcessorTypes } from '@portal/entity-services/payment-contracts/src/lib/components/form/processor-type.list';
import { TransactionFilterList } from '../transaction-filters.list';
import { FiltersService } from '@portal/shared/ui/filter/src/lib/services/filters.service';
import { SupportedProcessorSettingsService } from '@apps/portal/src/app/services/supported-processor-settings.service';
import { TransactionFilter } from '../../../enums/transaction-filter.enum';
import cloneDeep from 'lodash/cloneDeep';
import { Subscription } from 'rxjs';
import { Directions } from '@portal/shared/core/src/lib/enums/direction.enum';

@Component({
  selector: 'portal-transaction-filter-processor',
  templateUrl: './processor.component.html',
  providers: [
    {
      provide: FILTER_CONTENT_ITEM,
      useExisting: TransactionProcessorComponent,
    },
  ],
  standalone: false,
})
export class TransactionProcessorComponent extends FilterStaticBase {
  @Input()
  isMainDropdownFilter: boolean;
  @Input()
  showFilterSubMenu: boolean;
  @Output()
  processorTextName: EventEmitter<string> = new EventEmitter();
  processorText = this.transactionfilters.list.find(
    (filter) => filter.id === TransactionFilter.Processor,
  ).text;
  readonly Directions = Directions;

  constructor(
    private processorTypes: ProcessorTypes,
    private transactionfilters: TransactionFilterList,
    private filtersService: FiltersService,
    private supportedProcessorSettingService: SupportedProcessorSettingsService,
  ) {
    // Get processor configuration (selector and list)
    const config = processorTypes.getProcessorConfiguration();
    const selector = config.selector === 'destinationProcessor' ? SearchSelector.DestinationProcessor : SearchSelector.SourcePim;
    super(selector, config.processors);

    this.filtersService.actionRemoveFilter$.subscribe((entityToRemove: ISelectInput) => {
      if (this.searchSelector === entityToRemove.key) {
        const selectedEntity = this.localPreSelectedEntities.get(entityToRemove.id);
        if (selectedEntity?.id) {
          this.onEntityRemoved(entityToRemove);
          this.onFilterApplied();
          this.itemToggled.emit();
        }
      }
    });

    this.intermediatePreSelectedEntities = new Map();
    this.addButtonLabel = $localize`Add`;

    this.supportedProcessorSettingService.isProcessorFetched$.subscribe(() => {
      // Re-fetch configuration when processor settings change
      const config = this.processorTypes.getProcessorConfiguration();
      this.searchSelector = config.selector === 'destinationProcessor' ? SearchSelector.DestinationProcessor : SearchSelector.SourcePim;
      this.setAllEntities(config.processors);
    });

    this.subMenuComponentSelected$.subscribe((submenu) => {
      if (this.searchSelector.includes(submenu)) {
        this.synchronizeLocalPreSelectedEntities();
      }
      this.updateEntities();
    });

    this.updateEntities$.subscribe(() => {
      if (this.isMainDropdownFilter) {
        this.synchronizeLocalPreSelectedEntities();
      }
      this.updateEntities();
    });

    this.updateCounter$.subscribe(() => {
      const localPreSelected: Map<string, ISelectInput> = new Map();
      if (this.criteriaMapPreSelected.has(this.searchSelector)) {
        const argumentArr = this.getArgumentArray(
          this.criteriaMapPreSelected,
          this.searchSelector,
        );

        if (this.criteriaMapPreSelected.get(this.searchSelector)?.argument) {
          argumentArr?.forEach((argument) => {
            localPreSelected.set(argument, {
              id: argument,
              text: this.processorTypes.getValue(argument),
            });
          });
          this.localPreSelectedEntities = cloneDeep(localPreSelected);
        } else {
          this.localPreSelectedEntities.clear();
        }
      }
      this.updateEntities();
    });
  }

  addIntermediateEntity(selectedEntity: ISelectInput): void {
    this.intermediatePreSelectedEntities.set(selectedEntity.id, selectedEntity);
    this.setAddButtonLabel();
  }

  onEntityRemoved(selectedEntity: ISelectInput): void {
    this.intermediatePreSelectedEntities.delete(selectedEntity.id);
    this.setAddButtonLabel();
  }

  onFilterAddedAndEntitiesUpdated(): void {
    this.entities = this.processorTypes.list;
    this.localPreSelectedEntities.clear();
    this.intermediatePreSelectedEntities.forEach((entity) => {
      this.localPreSelectedEntities.set(entity.id, entity);
    });
    this.onFilterAdded();
    this.updateEntities();
    if (this.isOpen) {
      this.closeSubmenus$.next(true);
    }
    this.processorTextName.emit(this.processorText);
    this.isOpen = false;
  }

  onFilterAdded(): void {
    this.updateEntities();
    const values: string[] = this.getValues();
    if (values.length) {
      this.filterUpdated.emit({
        key: this.searchSelector,
        selector: this.searchSelector,
        operator: SearchOperator.In,
        value: Array.from(this.localPreSelectedEntities.values()),
        argument: `(${Array.from(values).join(',')})`,
      });
      this.staticFilterUpdated.emit({
        key: this.searchSelector,
        selector: this.searchSelector,
        value: Array.from(this.localPreSelectedEntities.values()),
      });
    } else {
      this.filterUpdated.emit({
        key: this.searchSelector,
        selector: this.searchSelector,
        toRemove: true,
      });
      this.staticFilterUpdated.emit({
        key: this.searchSelector,
        selector: this.searchSelector,
        toRemove: true,
      });
    }

    this.searchedText = '';
    this.itemToggled.emit();
    this.updateSwitchToggleState();
  }

  protected override subscribeToRemoveEntityObservable(): Subscription {
    return this.entityRemoved$.subscribe((entityToRemove: ISelectInput) => {
      const selectedEntity = this.localPreSelectedEntities.get(entityToRemove.id);
      if (selectedEntity) {
        this.onEntityRemoved(entityToRemove);
        this.onFilterAdded();
        this.isOpen = false;
      }
    });
  }

  private synchronizeLocalPreSelectedEntities(): void {
    if (this.criteriaMapPreSelected?.get(this.searchSelector)?.argument) {
      const argumentArr = this.getArgumentArray(this.selectedEntities, this.searchSelector);
      this.localPreSelectedEntities.clear();
      this.intermediatePreSelectedEntities.clear();
      argumentArr?.forEach((argument) => {
        this.setLocalPreSelectedEntities(argument, this.processorTypes.getValue(argument));
      });
      this.localPreSelectedEntities.forEach((entity) => {
        this.intermediatePreSelectedEntities.set(entity.id, entity);
      });
    } else {
      this.localPreSelectedEntities.clear();
      this.intermediatePreSelectedEntities.clear();
    }
    this.updateEntities();
    this.setAddButtonLabel();
  }
  onClear(): void {
    this.clearStaticMultiSelect();
    this.showSelectedEntities.clear();
    this.entities = this.processorTypes.getSupportedProcessors();
    this.removeStaticFilter();
    this.processorTextName.emit(this.processorText);
  }
}
